import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CaseManagementService } from '../case-management.service';
import { AlfrescoRepoBrowseService, RepoNode } from '../../wf-runtime/services/alfresco-repo-browse.service';

interface Crumb { id: string; name: string; }

/**
 * Case documents actions (phase 3): upload from the workstation, or add from the Alfresco
 * repository (browse → link a whole folder or selected files). The document-type "label" is
 * optional and comes from Reference Data (case-document-types). Standalone so it drops into the
 * existing Documents tab toolbar without touching the rest of the detail template/SCSS.
 */
@Component({
  selector: 'sp-case-doc-actions',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="cda">
      <div class="cda-bar">
        <label class="cda-lbl">Label
          <select [(ngModel)]="label">
            <option value="">— optional —</option>
            <option *ngFor="let o of docTypes" [value]="o.value">{{ o.label }}</option>
          </select>
        </label>
        <button class="btn btn-secondary" (click)="fileInput.click()" [disabled]="busy">
          <span class="material-icons">upload_file</span> Upload from computer
        </button>
        <input #fileInput type="file" multiple hidden (change)="onPick($event)">
        <button class="btn btn-secondary" (click)="toggleBrowser()" [disabled]="busy">
          <span class="material-icons">folder_open</span> Add from repository
        </button>
        <span class="cda-msg" *ngIf="msg">{{ msg }}</span>
        <span class="cda-err" *ngIf="err">{{ err }}</span>
      </div>

      <div class="cda-browser" *ngIf="browsing">
        <div class="cda-crumbs">
          <ng-container *ngFor="let c of crumbs; let i = index">
            <a (click)="crumbTo(i)">{{ c.name }}</a>
            <span class="sep" *ngIf="i < crumbs.length - 1">/</span>
          </ng-container>
        </div>
        <div class="cda-actions">
          <button class="btn btn-primary" (click)="linkFolder()" [disabled]="busy || crumbs.length < 2">
            Link this whole folder
          </button>
          <button class="btn btn-primary" (click)="linkSelected()" [disabled]="busy || !sel.size">
            Link selected ({{ sel.size }})
          </button>
          <button class="btn ghost" (click)="browsing = false">Close</button>
        </div>
        <div class="cda-list">
          <div *ngIf="loading" class="muted">Loading…</div>
          <div class="cda-row" *ngFor="let n of nodes">
            <a class="fold" *ngIf="n.isFolder" (click)="enter(n)">
              <span class="material-icons">folder</span>{{ n.name }}
            </a>
            <label class="file" *ngIf="!n.isFolder">
              <input type="checkbox" [checked]="sel.has(n.id)" (change)="toggle(n.id)">
              <span class="material-icons">description</span>{{ n.name }}
            </label>
          </div>
          <div *ngIf="!loading && !nodes.length" class="muted">This folder is empty.</div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .cda { width:100%; }
    .cda-bar { display:flex; align-items:flex-end; gap:12px; flex-wrap:wrap; }
    .cda-lbl { display:flex; flex-direction:column; font-size:11px; color:#6b7a8d; gap:4px; }
    .cda-lbl select { padding:7px 10px; border:1px solid #cdd8e3; border-radius:6px; font-size:13px; }
    .btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:6px; cursor:pointer; font-size:13px; border:1px solid #cdd8e3; background:#fff; color:#1f3b57; }
    .btn .material-icons { font-size:18px; }
    .btn-primary { background:#1f3b57; color:#fff; border:none; }
    .btn.ghost { background:#fff; }
    .btn:disabled { opacity:.5; cursor:default; }
    .cda-msg { color:#1c8a4d; font-size:12px; align-self:center; }
    .cda-err { color:#c0392b; font-size:12px; align-self:center; }
    .cda-browser { margin-top:12px; border:1px solid #cdd8e3; border-radius:8px; background:#fbfdff; padding:12px 14px; }
    .cda-crumbs { font-size:13px; margin-bottom:8px; }
    .cda-crumbs a { color:#2e5575; cursor:pointer; } .cda-crumbs a:hover { text-decoration:underline; }
    .cda-crumbs .sep { color:#aebccb; margin:0 6px; }
    .cda-actions { display:flex; gap:8px; margin-bottom:10px; flex-wrap:wrap; }
    .cda-list { max-height:300px; overflow:auto; border-top:1px solid #eef2f6; }
    .cda-row { padding:6px 4px; border-bottom:1px solid #f1f5f9; font-size:13px; }
    .cda-row .fold, .cda-row .file { display:flex; align-items:center; gap:8px; cursor:pointer; }
    .cda-row .fold { color:#2e5575; } .cda-row .material-icons { font-size:18px; color:#5a7fa6; }
    .muted { color:#8190a1; font-size:13px; padding:8px 4px; }
  `]
})
export class CaseDocActionsComponent implements OnInit {
  @Input() caseRef = '';
  @Output() changed = new EventEmitter<void>();

  private readonly svc = inject(CaseManagementService);
  private readonly browse = inject(AlfrescoRepoBrowseService);

  docTypes: { value: string; label: string }[] = [];
  label = '';
  busy = false; msg = ''; err = '';

  browsing = false; loading = false;
  crumbs: Crumb[] = [{ id: '-root-', name: 'Company Home' }];
  nodes: RepoNode[] = [];
  sel = new Set<string>();

  ngOnInit(): void {
    this.svc.referenceItems('case-document-types').subscribe(x => this.docTypes = x);
  }

  async onPick(ev: Event): Promise<void> {
    const input = ev.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (!files.length) { return; }
    this.busy = true; this.err = ''; this.msg = '';
    let ok = 0;
    try {
      for (const f of files) {
        await firstValueFrom(this.svc.uploadDocument(this.caseRef, f, this.label || undefined));
        ok++;
      }
      this.msg = `Uploaded ${ok} file(s).`;
      this.changed.emit();
    } catch (e: any) {
      this.err = e?.error?.message || e?.message || 'Upload failed.';
    } finally {
      this.busy = false; input.value = '';
    }
  }

  toggleBrowser(): void {
    this.browsing = !this.browsing;
    this.err = ''; this.msg = '';
    if (this.browsing) { this.crumbs = [{ id: '-root-', name: 'Company Home' }]; this.sel.clear(); this.load('-root-'); }
  }

  private async load(id: string): Promise<void> {
    this.loading = true;
    try { this.nodes = await this.browse.listChildren(id); }
    catch (e: any) { this.err = e?.message || 'Could not browse the repository.'; this.nodes = []; }
    finally { this.loading = false; }
  }

  enter(n: RepoNode): void { this.crumbs.push({ id: n.id, name: n.name }); this.sel.clear(); this.load(n.id); }
  crumbTo(i: number): void { this.crumbs = this.crumbs.slice(0, i + 1); this.sel.clear(); this.load(this.crumbs[i].id); }
  toggle(id: string): void { this.sel.has(id) ? this.sel.delete(id) : this.sel.add(id); }

  async linkFolder(): Promise<void> {
    await this.doLink({ folderId: this.crumbs[this.crumbs.length - 1].id, label: this.label || undefined });
  }
  async linkSelected(): Promise<void> {
    await this.doLink({ nodeIds: Array.from(this.sel), label: this.label || undefined });
  }

  private async doLink(body: { nodeIds?: string[]; folderId?: string; label?: string }): Promise<void> {
    this.busy = true; this.err = ''; this.msg = '';
    try {
      const linked = await firstValueFrom(this.svc.linkDocuments(this.caseRef, body));
      this.msg = `Linked ${linked.length} document(s).`;
      this.browsing = false; this.sel.clear();
      this.changed.emit();
    } catch (e: any) {
      this.err = e?.error?.message || e?.message || 'Link failed.';
    } finally {
      this.busy = false;
    }
  }
}
